<template>
  <AppPageFrame>
    <LoadingPage v-if="loginState.inProgress"/>
    
    <div v-else class="loginContainer is-flex-grow-1">
      <div class="loginImageMobile"></div>

        <form class="loginForm" @submit.prevent>

          <router-link to="/">
            <img alt="Minicity Logo" src="@/assets/images/minicity_logo.png" class="logoImage ml-3 mr-3"/>
          </router-link>

          <div class="p-4">
            <div class="infoNote">
              <p>This site is for citizens to access Mini City services.</p>
              <p class="pt-1">Caseworkers / partners, please use <a class="infoLink" href="https://portal.minicityatl.com">portal.minicityatl.com</a>.</p>
            </div>
          </div>

          <div class="pt-5">
            <label>Username</label>
            <div>
              <input type="text" placeholder="Username" class="textInput" autofocus v-model="loginState.username" />
            </div>
          </div>

          <div class="pt-5">
            <label>Password</label>
            <div>
              <input type="password" placeholder="Password" class="textInput" v-model="loginState.password" />
            </div>
            <a class="forgotPasswordLink" @click="resetPassword()">Forgot your password?</a>
          </div>

          <div class="pt-5">
            <div>
              <button :disabled="!isFormValid()" class="p-3 mt-3 ctaButton" @click="doLogin()">Login</button>
            </div>
          </div>

          <div v-if="CONFIG!.maintenanceMessage && CONFIG!.maintenanceMessage.length > 0" class="notification bulmaNotification is-warning">{{ CONFIG!.maintenanceMessage }}</div>
          <div v-if="loginState.error.length > 0" class="errorNotification">{{ loginState.error }}</div>
          <div v-if="loginState.isAutoLogout" class="errorNotification">You have been automatically logged-out due to inactivity</div>
        </form>
   

      <div class="loginImageDesktop"></div>
    </div>
  </AppPageFrame>

  <ModalDialog
    :active="passwordResetState.shown"
    title="Reset Password"
    :allow-close="true"
    @close="passwordResetState.shown = false"
    first-button-text="Go Back"
    first-button-severity="link"
    @firstbuttonselected="passwordResetState.shown = false"
    second-button-text="Reset"
    second-button-severity="primary"
    @secondbuttonselected="doResetPassword()"
  >
    <div>
      <div v-if="passwordResetState.inProgress" style="text-align: center">
        <LoadingSpinner size="medium"/>
      </div>
      <div v-else class="columns is-loading">
        <div class="pt-5">
          <label>Please enter your username to reset your password</label>
          <div>
            <input type="text" placeholder="Username" class="textInput" :class="{ 'is-danger': loginState.error.length > 0 }" autofocus v-model="passwordResetState.username" />
          </div>
          <p class="p-4"><strong>NOTE: </strong> If you didn't provide an email address when registering with Mini City, you'll need to ask your caseworker to reset your password for you.</p>
        </div>
      </div>
    </div>
    
  </ModalDialog>
</template>

<script lang="ts" setup>
import { reactive, getCurrentInstance, onMounted } from "vue";
import { useRoute } from "vue-router";
import { login, initiatePasswordReset } from "@/core/session"
import { mapHttpError, notifyError, notifyNetworkError, notifySuccess } from "@/core/utils";
import { CONFIG } from "@/core/config";
import { Validations } from "@/composables/useValidation";

const instance = getCurrentInstance();

const forwardTo = useRoute().query.forwardTo;
const postLoginURL = forwardTo?.length ? forwardTo.toString() : '/';

const loginState = reactive({
  username: "",
  password: "",
  inProgress: false,
  error: "",
  isAutoLogout: useRoute().query.autoLogout == 'true'
});

const doLogin = () => {
  loginState.isAutoLogout = false;
  loginState.error = "";
  loginState.inProgress = true;

  login(loginState.username, loginState.password)
    .then(sess => {
      instance!.proxy!.$router.push(postLoginURL);
    })
    .catch(err => {
      loginState.error = err.response.status == 401 ? "Invalid username or password" : mapHttpError(err).message;
    })
    .finally(() => {
      loginState.inProgress = false;
    });
};

const isFormValid = (): boolean => {
  return (Validations.notEmpty(loginState.username) && Validations.notEmpty(loginState.password));
};

const passwordResetState = reactive({
  inProgress: false,
  shown: false,
  username: "",
});

const resetPassword = () => {
  passwordResetState.shown = true;
  passwordResetState.username = loginState.username;
};

const doResetPassword = () => {
  if (!(Validations.notEmpty(passwordResetState.username))) {
    notifyError("Please enter your username");
    return;
  }

  passwordResetState.inProgress = true;

  initiatePasswordReset(passwordResetState.username)
  .then(() => {
    notifySuccess("Please check your inbox for a link to reset your password!");
    passwordResetState.shown = false;
  })
  .catch(err => notifyNetworkError("Failed to reset password", err))
  .finally(() => passwordResetState.inProgress = false);
};

</script>

<style lang="scss" scoped>
@import "../assets/variables.scss";

.loginContainer {
  display: flex;
  flex-direction: row;
  align-items: center;
}

.loginImageMobile {
  display: none;
  background-image: url('../assets/images/loginImage_mobile.jpg');
  background-size: cover;
  background-position: center;
  width: 100%;
  height: 15vh;
  margin-bottom: 2rem;
}

.loginImageDesktop {
  flex-grow: 1;
  width: 30vh;
  background-image: url('../assets/images/loginImage_desktop.jpg');
  background-size: cover;
  border-radius: 1.5rem;
  margin: 3rem 4rem 3rem 3rem;
  min-height: 70vh;
}

.loginForm {
  flex-grow: 1;

  display: flex;
  flex-direction: column;
  align-items: center;
}

.checkboxGroup {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 0.75rem;
}

@media screen and (max-width: $tabletWidth) {
  .logoImage {
    width: 200px;
    height: auto;
  }

  .loginContainer {
    flex-direction: column;
    padding-bottom: 1rem;
  }

  .loginImageDesktop {
    display: none;
  }

  .loginImageMobile {
    display: block;
  }
}

.textInput { 
  padding: 0.75rem;
  margin-top: 0.5rem;
  font-weight: 700;
  font-size: 100%;
  width: 100%;
  border-radius: 0.25rem;
  border: 1px solid black;
}

.loginButton {
  background-color: $primary;
  border-radius: 0.25rem;
}

.forgotPasswordLink {
  display:block;
  text-align: right;
  font-size: small;
  text-decoration: underline;
}

.errorNotification {
  color: red;
  padding: 1rem;
}


</style>

<style>
.logoImage {
  width: 250px;
  height: auto;
}

.infoNote {
  background-color: #111111;
  color: white;
  padding: 1rem;
  border-radius: 1rem;
  font-size: 120%;
}

.infoLink {
  color: white;
  text-decoration: underline;
  font-weight: bold;
}

.pageWrapper {
  padding: 0rem !important;
}

.contentWrapper.pt-5 {
  padding: 0rem !important;
  padding-top: 0rem !important;
}
</style>
